import React, { useEffect, useRef, useState, useCallback } from "react";
import { createChart } from "lightweight-charts";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { RefreshCw, TrendingUp, TrendingDown } from "lucide-react";

// Converts ISO string → unix seconds (lightweight-charts needs this)
function toUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

function fmt(v, d = 2) {
  if (v == null || !isFinite(v)) return "—";
  return Number(v).toFixed(d);
}

function pct(v) {
  if (v == null || !isFinite(v)) return "—";
  const n = Number(v);
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(2)}%`;
}

function SignalRow({ label, value, max = 1, color }) {
  const pct = max > 0 ? Math.abs(value / max) * 100 : 0;
  const isPos = value >= 0;
  return (
    <div className="signal-bar">
      <span className="s-name">{label}</span>
      <div className="s-track">
        <div
          className="s-fill"
          style={{
            width: `${Math.min(pct / 2, 50)}%`,
            background: isPos ? "var(--green)" : "var(--red)",
            left: isPos ? "50%" : `${50 - Math.min(pct / 2, 50)}%`,
          }}
        />
      </div>
      <span className="s-val" style={{ color: isPos ? "var(--green)" : "var(--red)" }}>
        {fmt(value, 3)}
      </span>
    </div>
  );
}

export function MarketsWorkspace() {
  const { state } = useAtlas();
  const { provider, symbol, interval, period } = state;

  const chartRef = useRef(null);
  const chartInst = useRef(null);
  const seriesRef = useRef(null);
  const volSeriesRef = useRef(null);

  const [bars, setBars] = useState([]);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [servedBy, setServedBy] = useState(null);
  const [lastBar, setLastBar] = useState(null);

  // Diagnostic logger
  const [diagLogs, setDiagLogs] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDiagLogs(curr => [...curr, `fetchData triggered [symbol=${symbol}, interval=${interval}]`]);
    try {
      setDiagLogs(curr => [...curr, `Sending HTTP GET request to backend: ${API}/market/ohlcv`]);
      const mktPromise = axios.get(`${API}/market/ohlcv`, { params: { provider, symbol, interval, period, limit: 750 } });
      mktPromise.then(
        (r) => setDiagLogs(curr => [...curr, `ohlcv: Resolved! (${r.data?.bars?.length || 0} bars found)`]),
        (err) => setDiagLogs(curr => [...curr, `ohlcv: Rejected! (${err.message})`])
      );

      setDiagLogs(curr => [...curr, `Sending HTTP GET request to backend: ${API}/analysis/snapshot`]);
      const snapPromise = axios.get(`${API}/analysis/snapshot`, { params: { provider, symbol, interval, period: "6mo" } });
      snapPromise.then(
        () => setDiagLogs(curr => [...curr, `snapshot: Resolved!`]),
        (err) => setDiagLogs(curr => [...curr, `snapshot: Rejected! (${err.message})`])
      );

      const [mktRes, snapRes] = await Promise.allSettled([mktPromise, snapPromise]);
      setDiagLogs(curr => [...curr, `Promise.allSettled finished [mkt=${mktRes.status}, snap=${snapRes.status}]`]);

      if (mktRes.status === "fulfilled") {
        const d = mktRes.value.data;
        setBars(d.bars || []);
        setServedBy(d.served_by);
        if (d.bars?.length) setLastBar(d.bars[d.bars.length - 1]);
      } else {
        setError("Failed to load market data: " + (mktRes.reason?.message || JSON.stringify(mktRes.reason)));
      }

      if (snapRes.status === "fulfilled") {
        setSnap(snapRes.value.data);
      } else {
        console.error("Snap failed:", snapRes.reason);
      }
    } catch (e) {
      setDiagLogs(curr => [...curr, `Exception caught: ${e.message}`]);
      setError(e.message);
    }
    setLoading(false);
  }, [provider, symbol, interval, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Initialize chart
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = createChart(chartRef.current, {
      layout: {
        background: { color: "transparent" },
        textColor: "#4a6070",
        fontSize: 10,
        fontFamily: "'JetBrains Mono', monospace",
      },
      grid: {
        vertLines: { color: "#1a2233" },
        horzLines: { color: "#1a2233" },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: "#2d3f58", width: 1, style: 3 },
        horzLine: { color: "#2d3f58", width: 1, style: 3 },
      },
      rightPriceScale: {
        borderColor: "#1a2233",
        textColor: "#4a6070",
      },
      timeScale: {
        borderColor: "#1a2233",
        textColor: "#4a6070",
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: true,
      handleScale: true,
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#22c55e",
      downColor: "#f43f5e",
      borderUpColor: "#22c55e",
      borderDownColor: "#f43f5e",
      wickUpColor: "#22c55e",
      wickDownColor: "#f43f5e",
    });

    const volSeries = chart.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
      color: "#1c2535",
    });
    chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    });

    chartInst.current = chart;
    seriesRef.current = candleSeries;
    volSeriesRef.current = volSeries;

    const ro = new ResizeObserver(() => {
      if (chartRef.current) {
        chart.applyOptions({
          width: chartRef.current.clientWidth,
          height: chartRef.current.clientHeight,
        });
      }
    });
    ro.observe(chartRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartInst.current = null;
    };
  }, []);

  // Feed data into chart
  useEffect(() => {
    if (!seriesRef.current || !bars.length) return;
    const candles = bars.map(b => ({
      time: toUnix(b.time),
      open: b.open, high: b.high, low: b.low, close: b.close,
    })).sort((a, b) => a.time - b.time);

    const vols = bars.map(b => ({
      time: toUnix(b.time),
      value: b.volume,
      color: b.close >= b.open ? "rgba(34,197,94,0.25)" : "rgba(244,63,94,0.25)",
    })).sort((a, b) => a.time - b.time);

    seriesRef.current.setData(candles);
    volSeriesRef.current.setData(vols);
    chartInst.current?.timeScale().fitContent();
  }, [bars]);

  const snapshot = snap?.snapshot || {};
  const open = lastBar?.open;
  const close = lastBar?.close;
  const changeAbs = close != null && open != null ? close - open : null;
  const changePct = close != null && open != null ? ((close - open) / open) * 100 : null;
  const isUp = changePct != null ? changePct >= 0 : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", height: "100%", gap: 4, padding: 4 }}>
      {/* Main chart column */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minHeight: 0 }}>
        {/* Chart header */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 4px", flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }}>
            {symbol}
          </span>
          {close != null && (
            <span style={{ fontSize: 16, fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)", fontWeight: 600 }}>
              {fmt(close, 4)}
            </span>
          )}
          {changePct != null && (
            <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: isUp ? "var(--green)" : "var(--red)", fontWeight: 500 }}>
              {isUp ? "+" : ""}{fmt(changeAbs, 4)} ({pct(changePct)})
            </span>
          )}
          {servedBy && servedBy !== provider && (
            <span className="badge badge-amber">fallback: {servedBy}</span>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            <button
              className="btn btn-ghost btn-icon"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw size={12} className={loading ? "spin" : ""} />
            </button>
          </div>
        </div>

        {/* Chart */}
        <div className="panel" style={{ flex: 1, minHeight: 0, padding: 0, overflow: "hidden", position: "relative" }}>
          {error && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--red)", fontSize: 12, zIndex: 5 }}>
              {error}
            </div>
          )}
          {loading && !bars.length && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(15,17,23,0.9)", color: "var(--text-tertiary)", fontSize: 11, zIndex: 10, padding: 20 }}>
              <div style={{ marginBottom: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-primary)" }}>Loading Workstation Data</div>
              <div style={{ width: "100%", maxWidth: 450, background: "var(--bg)", border: "1px solid var(--border-dim)", borderRadius: 4, padding: "8px 12px", fontFamily: "monospace", fontSize: 9, textAlign: "left", display: "flex", flexDirection: "column", gap: 3, maxHeight: 150, overflowY: "auto" }}>
                {diagLogs.map((log, i) => (
                  <div key={i} style={{ color: log.includes("Rejected") || log.includes("Failed") ? "var(--red)" : log.includes("Resolved") ? "var(--green)" : "var(--text-secondary)", lineHeight: 1.3 }}>
                    &gt; {log}
                  </div>
                ))}
                {diagLogs.length === 0 && <div>Initializing logs...</div>}
              </div>
            </div>
          )}
          <div ref={chartRef} style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }} />
        </div>
      </div>

      {/* Right info rail */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minHeight: 0, overflowY: "auto" }}>

        {/* OHLCV summary */}
        <div className="panel">
          <div className="panel-header"><span className="panel-title">Last Bar</span></div>
          <div className="panel-body" style={{ padding: "6px 10px" }}>
            {lastBar ? (
              <div>
                {[
                  ["Open",   lastBar.open],
                  ["High",   lastBar.high],
                  ["Low",    lastBar.low],
                  ["Close",  lastBar.close],
                  ["Volume", lastBar.volume],
                ].map(([k, v]) => (
                  <div className="kv-row" key={k}>
                    <span className="k">{k}</span>
                    <span className="v">{k === "Volume" ? Number(v).toLocaleString("en", { maximumFractionDigits: 0 }) : fmt(v, 4)}</span>
                  </div>
                ))}
                <div className="kv-row">
                  <span className="k">Bars</span>
                  <span className="v">{bars.length}</span>
                </div>
              </div>
            ) : <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>Loading…</div>}
          </div>
        </div>

        {/* Analysis snapshot */}
        <div className="panel" style={{ flex: 1, minHeight: 0 }}>
          <div className="panel-header"><span className="panel-title">Market Analysis</span></div>
          <div className="panel-body no-pad" style={{ overflowY: "auto" }}>
            {snap ? (
              <div style={{ padding: "6px 10px" }}>
                {/* Regime */}
                {snapshot.regime && (
                  <div style={{ marginBottom: 8 }}>
                    <div className="section-sep"><span className="s-label">Regime</span></div>
                    {[
                      ["Trend",    snapshot.regime?.trend_label],
                      ["Vol",      snapshot.regime?.volatility_label],
                      ["Market",   snapshot.regime?.market_structure],
                      ["Phase",    snapshot.regime?.cycle_phase],
                    ].map(([k, v]) => v != null && (
                      <div className="kv-row" key={k}>
                        <span className="k">{k}</span>
                        <span className="v" style={{ fontFamily: "Inter", fontSize: 11, textTransform: "capitalize" }}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Momentum signals */}
                {snapshot.signals && (
                  <div style={{ marginBottom: 8 }}>
                    <div className="section-sep"><span className="s-label">Signals</span></div>
                    {Object.entries(snapshot.signals || {})
                      .filter(([k, v]) => typeof v === "number")
                      .slice(0, 12)
                      .map(([k, v]) => (
                        <SignalRow key={k} label={k.replace(/_/g, " ")} value={v} max={3} />
                      ))}
                  </div>
                )}

                {/* Macro stats */}
                {snapshot.macro && (
                  <div>
                    <div className="section-sep"><span className="s-label">Macro</span></div>
                    {Object.entries(snapshot.macro || {})
                      .filter(([k, v]) => typeof v === "number")
                      .slice(0, 8)
                      .map(([k, v]) => (
                        <div className="kv-row" key={k}>
                          <span className="k">{k.replace(/_/g, " ")}</span>
                          <span className="v" style={{ color: v > 0 ? "var(--green)" : v < 0 ? "var(--red)" : "var(--text-primary)" }}>
                            {fmt(v, 4)}
                          </span>
                        </div>
                      ))}
                  </div>
                )}

                {/* Explanation */}
                {snap.explanation && (
                  <div style={{ marginTop: 8 }}>
                    <div className="section-sep"><span className="s-label">Interpretation</span></div>
                    {Object.entries(snap.explanation)
                      .filter(([, v]) => typeof v === "string")
                      .slice(0, 6)
                      .map(([k, v]) => (
                        <div key={k} style={{ padding: "3px 0", borderBottom: "1px solid var(--border-dim)" }}>
                          <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "capitalize", marginBottom: 1 }}>{k.replace(/_/g, " ")}</div>
                          <div style={{ fontSize: 10, color: "var(--text-secondary)" }}>{v}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: 10, color: "var(--text-tertiary)", fontSize: 11 }}>
                {loading ? "Loading analysis…" : "No snapshot available"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
